import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { SmrFilterOption } from "component/mapping/result/SMRResultsFilters"
import { FC, KeyboardEvent, useRef, useState } from "react"
import { FaCheck, FaChevronDown, FaSearch } from "react-icons/fa"

interface SMRSectionSelectProps {
  // Первый вариант — "Все разделы"
  options: SmrFilterOption[]
  value: string
  onChange: (value: string) => void
}

// Выбор раздела сметы: разделов десятки, поэтому с поиском.
// Кнопка выглядит как остальные фильтры-пилюли и подсвечивается, когда
// выбран конкретный раздел
export const SMRSectionSelect: FC<SMRSectionSelectProps> = (props) => {
  const { options, value, onChange } = props
  const [allOption, ...sectionOptions] = options

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [search, setSearch] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = options.find((option) => option.value === value) || allOption
  const isFiltered = selected.value !== allOption.value

  const foundOptions = sectionOptions.filter((option) =>
    option.label.toLowerCase().includes(search.trim().toLowerCase()),
  )

  // Поиск сбрасываем при любом закрытии, чтобы в следующий раз был виден весь список
  const handleClose = () => {
    onClose()
    setSearch("")
  }

  const handleSelect = (newValue: string) => {
    onChange(newValue)
    handleClose()
  }

  // Enter — выбрать первый найденный раздел
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && foundOptions.length > 0) {
      handleSelect(foundOptions[0].value)
    }
  }

  const renderOption = (option: SmrFilterOption) => {
    const isSelected = option.value === selected.value
    return (
      <Flex
        key={option.value}
        as="button"
        type="button"
        w="full"
        align="center"
        gap={2}
        px={3}
        py={2}
        borderRadius="md"
        textAlign="left"
        fontSize="sm"
        bg={isSelected ? "blue.50" : "transparent"}
        color={isSelected ? "blue.700" : "inherit"}
        _hover={{ bg: isSelected ? "blue.50" : "gray.100" }}
        onClick={() => handleSelect(option.value)}
      >
        <Box w={3} flexShrink={0}>
          {isSelected && <FaCheck size={11} />}
        </Box>
        <Text flex={1} noOfLines={1}>
          {option.label}
        </Text>
        <Text color="gray.500" fontSize="xs">
          {option.count}
        </Text>
      </Flex>
    )
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      placement="bottom-start"
      initialFocusRef={searchRef}
      isLazy
    >
      <PopoverTrigger>
        <Button
          size="sm"
          maxW="340px"
          borderRadius="full"
          fontWeight="medium"
          colorScheme={isFiltered ? "blue" : "gray"}
          variant={isFiltered ? "solid" : "outline"}
          rightIcon={<FaChevronDown size={10} />}
        >
          <Text noOfLines={1}>
            {selected.label} · {selected.count}
          </Text>
        </Button>
      </PopoverTrigger>

      <PopoverContent w="360px" boxShadow="lg">
        <PopoverBody p={2}>
          <InputGroup size="sm" mb={2}>
            <InputLeftElement pointerEvents="none" color="gray.400">
              <FaSearch size={12} />
            </InputLeftElement>
            <Input
              ref={searchRef}
              borderRadius="md"
              placeholder="Найти раздел"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </InputGroup>

          <Box maxH="320px" overflowY="auto">
            {!search && (
              <>
                {renderOption(allOption)}
                <Divider my={1} />
              </>
            )}

            {foundOptions.map(renderOption)}

            {foundOptions.length === 0 && (
              <Text px={3} py={4} fontSize="sm" color="gray.500" textAlign="center">
                Такого раздела нет
              </Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
